// Per-run site table over the filtered selection. Clicking a row flies the map
// to that segment; the caret expands the row to list the ACTUAL crashes within
// the buffer — each crash line flies the map to that crash — and, when the
// countermeasure layer is on, the CMF card with the benefit arithmetic.
import React, { useState } from 'react';
import CmfCard, { Stars } from './CmfCard';
import { fmtUsd } from './insightsText';
import { MultiPicker } from './CorridorPicker';

const SEVERITY_OPTIONS = [
  { key: 'severe', label: 'Severe' }, { key: 'moderate', label: 'Moderate' }, { key: 'minor', label: 'Minor' },
];

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

// The "worst" column means something different per analysis
const worstLabel = (analysis) => ({
  SSD: 'Worst (mph)', PSD: 'Worst (mph)', GLR: 'Glare (min/yr)',
  FSA: 'Cross slope (%)', HCA: 'Score (%)', GRW: 'Embank. ht (ft)',
  ISD: 'Required ISD (ft)', ALL: 'Worst',
}[analysis] || 'Worst');

const BUFFER_MI = 0.1;
const WORST_HELP = {
  SSD: 'Worst speed deficit (mph): how far the speed the sight distance supports falls below the regulatory speed',
  PSD: 'Worst speed deficit (mph) for passing sight distance',
  GLR: 'Glare minutes per year at the worst station (≥ 9,600 = impact section)',
  FSA: 'Cross slope (%) at the flattest station (below 0.5% = poor drainage)',
  HCA: 'Curve safety score (%) = safe speed ÷ posted speed',
  GRW: 'Embankment height (ft) at the worst station',
  ISD: 'Required AASHTO intersection sight distance (ft)',
};

export default function SitesTable({ sites, analysis, onFocus, crashes, showCmf, exposure, ratePct, showCorridor = false }) {
  const [openKey, setOpenKey] = useState(null);
  const [severities, setSeverities] = useState([]);   // [] = every severity band
  if (!sites?.length) return <div className="safety-muted">No {analysis} deficiency runs in the current selection.</div>;
  const visible = severities.length ? sites.filter((s) => severities.includes(s.worstSeverity)) : sites;
  const showAnalysis = analysis === 'ALL';
  const cmfOn = !!showCmf;

  const crashesFor = (s) => (crashes || []).filter(
    (c) => (c.corridor || '') === (s.corridor || '')
      && c.milepost >= s.mpStart - BUFFER_MI && c.milepost <= s.mpEnd + BUFFER_MI);

  // with the countermeasure layer on, rows sort by annual benefit
  const ordered = cmfOn
    ? [...visible].sort((a, b) => (b.cmf?.benefitPerYr || 0) - (a.cmf?.benefitPerYr || 0) || a.mpStart - b.mpStart)
    : visible;
  const colCount = 9 + (showAnalysis ? 1 : 0) + (cmfOn ? 4 : 0) + (showCorridor ? 1 : 0);

  return (
    <div>
      <div className="safety-insights-nav safety-sites-tools">
        <MultiPicker label="Severity" options={SEVERITY_OPTIONS} selected={severities}
          onChange={setSeverities} noun="severities" allMeansEmpty
          title="Show only runs in these severity bands" />
        <span className="safety-muted">{ordered.length} of {sites.length} runs</span>
      </div>
    <div className="safety-table-scroll">
      <table className="safety-table safety-table-clickable">
        <thead>
          <tr>
            <th />
            {showCorridor && <th>Corridor</th>}
            {showAnalysis && <th>Analysis</th>}
            <th title="Milepost range of the deficiency run">Milepost</th>
            <th title="Travel direction: EB = P (primary), WB = N">Dir</th>
            <th title="Number of deficient survey stations in the run (or the curve number for HCA)">Stations</th>
            <th title={WORST_HELP[analysis] || 'Worst station value in the run'}>{worstLabel(analysis)}</th>
            <th title="Run severity band: severe / moderate / minor from the worst station value">Severity</th>
            <th title="Run length in miles">Length</th>
            <th title="Crashes within ±0.1 mi of the run (the site buffer used everywhere in the dashboard)">Crashes</th>
            <th title="Fatal + injury crashes among them (KABCO K/A/B/C)">K + Inj</th>
            {cmfOn && (
              <>
                <th title="Recommended countermeasure (FHWA CMF Clearinghouse, best-rated entry under the CMF quality policy)">Countermeasure</th>
                <th title="Crash Modification Factor: expected crashes after ÷ before. 0.42 means 58% fewer crashes of the type the study covers. Stars = Clearinghouse quality rating (1–5).">CMF</th>
                <th title="Matching crashes at the site ÷ exposure years × (1 − CMF)">Prevented / yr</th>
                <th title="Each prevented crash valued at the state's KABCO comprehensive unit cost, per year">Benefit / yr</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {ordered.map((s) => {
            const key = `${s.corridor ? `${s.corridor}|` : ''}${s.analysis || analysis}-${s.direction}-${s.id}`;
            const open = openKey === key;
            const siteCrashes = open ? crashesFor(s) : [];
            const rec = s.cmf;
            return (
              <React.Fragment key={key}>
                <tr
                  className={s.crashCount > 0 ? 'safety-row-hit' : ''}
                  onClick={() => onFocus && onFocus({
                    bounds: (s.coords || []).filter((p) => p[0] != null),
                    lat: s.coords?.[0]?.[0], lng: s.coords?.[0]?.[1],
                    runKey: key, key: `site-${key}-${Date.now()}`,
                  })}
                  title="Show this segment on the map"
                >
                  <td onClick={(e) => { e.stopPropagation(); setOpenKey(open ? null : key); }}
                    className="safety-expander"
                    title={s.crashCount || rec ? 'Crashes at this site and the countermeasure arithmetic' : 'No crashes here'}>
                    {s.crashCount || (cmfOn && rec) ? (open ? '▾' : '▸') : ''}
                  </td>
                  {showCorridor && <td>{s.road || s.corridor}</td>}
                  {showAnalysis && <td>{s.analysis}</td>}
                  <td>{fmtMp(s.mpStart)}–{fmtMp(s.mpEnd)}</td>
                  <td>{s.direction === 'P' ? 'EB' : 'WB'}</td>
                  <td>{s.curveNumber != null ? `curve #${s.curveNumber}` : s.nPoints}</td>
                  <td>{s.worstValue}</td>
                  <td><span className={`safety-sev safety-sev-${s.worstSeverity}`}>{s.worstSeverity}</span></td>
                  <td>{s.lengthMi}</td>
                  <td>{s.crashCount ? <strong>{s.crashCount}</strong> : '·'}</td>
                  <td>{s.injuryCount || '·'}</td>
                  {cmfOn && (rec?.entry ? (
                    <>
                      <td className="safety-cell-wrap" title={rec.entry.appliesTo}>{rec.entry.name}</td>
                      <td title={`CMF ${rec.entry.cmf}: ${Math.round((1 - rec.entry.cmf) * 100)}% fewer ${rec.entry.appliesTo || 'crashes of the covered type'} expected after the countermeasure · ${rec.entry.stars}★ Clearinghouse rating${rec.entry.se != null ? ` · standard error ${rec.entry.se}` : ''} · ${rec.entry.study || ''}`}>{rec.entry.cmf} <Stars entry={rec.entry} /></td>
                      <td>{rec.preventedPerYr || '·'}</td>
                      <td>{rec.benefitPerYr ? <span className="safety-benefit">{fmtUsd(rec.benefitPerYr)}</span> : '·'}</td>
                    </>
                  ) : (
                    <>
                      <td className="safety-cell-wrap safety-muted" colSpan={4} title={rec?.note || ''}>
                        {rec?.excludedReason === 'existingGuardrail' ? 'Guardrail already in place — warrant validated'
                          : rec?.excludedReason === 'noCmf' ? 'No CMF for sun glare — advisory'
                            : rec?.excludedReason === 'policy' ? 'No CMF under this quality policy'
                              : rec ? rec.note : '—'}
                      </td>
                    </>
                  ))}
                </tr>
                {open && (siteCrashes.length > 0 || (cmfOn && rec)) && (
                  <tr className="safety-site-crashes">
                    <td colSpan={colCount}>
                      {cmfOn && rec && <CmfCard rec={rec} exposure={exposure} ratePct={ratePct} />}
                      {siteCrashes.length > 0 && (
                      <ul>
                        {siteCrashes.map((c) => (
                          <li key={c.id}
                            onClick={() => onFocus && onFocus({
                              lat: c.lat, lng: c.lon, zoom: 16,
                              crashId: c.id, key: `sitecrash-${c.id}-${Date.now()}`,
                            })}
                            title="Show this crash on the map">
                            <span className={`safety-sev safety-sev-${c.severityClass === 'fatal' ? 'severe' : c.severityClass === 'injury' ? 'moderate' : 'minor'}`}>
                              {c.severityClass}
                            </span>
                            {' '}MP {fmtMp(c.milepost)} · {c.datetime ? c.datetime.slice(0, 10) : '—'} · {c.manner || 'crash'}
                            {c.surface && c.surface !== 'Dry' ? ` · ${c.surface}` : ''}
                            {c.isAnimal ? ' · animal' : ''}
                          </li>
                        ))}
                      </ul>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
}
